import {singleMemo} from '@packages/utilities/memo';
import {incrementFluxMetric} from '@packages/systems/designer-telemetry';

import type {Dispatch, TFlux} from './types';

type Engine<SelectorResult, Action> = (
  dispatch: Dispatch<Action>
) => (results: SelectorResult) => void;
type Selector<State, SelectorResult> = (state: State) => SelectorResult;

export type EngineConfig<State, Action> = <EngineConfigWithResult>(
  createEngineConfig: <SelectorResult>(
    engineConfig: [
      Engine<SelectorResult, Action>,
      Selector<State, SelectorResult>,
    ]
  ) => EngineConfigWithResult
) => EngineConfigWithResult;

export const engineConfig =
  <State, Action, Result>(
    engine: Engine<Result, Action>,
    selector: Selector<State, Result>
  ): EngineConfig<State, Action> =>
  (f) =>
    f([engine, selector]);

export function initEngines<F extends TFlux<any, any, any>>(flux: F) {
  type State = F extends TFlux<infer S, any> ? S : never;
  type Actions = F extends TFlux<any, infer A> ? A : never;
  return (engineConfigs: EngineConfig<State, Actions>[]) => {
    const engineListeners = engineConfigs.map((conf) =>
      conf(([Engine, selector]) => {
        const engine = singleMemo(Engine(flux.dispatch));
        return (state: State) => engine(selector(state));
      })
    );

    flux.setRegisteredEngineCountForMetrics(engineListeners.length);

    flux.onChange(() => {
      const state = flux.state;
      const metricsCtx = flux.getMetricsContext?.();
      engineListeners.forEach((listener) => {
        // Track engine invocations (incrementFluxMetric is a no-op if ctx is undefined)
        incrementFluxMetric(metricsCtx, 'enginesInvokedCount');
        listener(state);
      });
    });
  };
}
